import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Menu } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/lib/context/auth-context";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  BarChart,
  Book,
  Calendar,
  CheckCircle2,
  ChevronDown,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  User,
  BadgeCheck,
  Users,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

const MobileNav = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();

  const mainLinks = [
    {
      label: "Dashboard",
      icon: LayoutDashboard,
      to: "/dashboard",
      activePath: "/dashboard",
      roles: ["user", "staff", "admin", "tech"],
    },
    {
      label: "Schedule",
      icon: Calendar,
      to: "/schedule",
      activePath: "/schedule",
      roles: ["user", "staff", "admin", "tech"],
    },
    {
      label: "Courses",
      icon: Book,
      to: "/courses",
      activePath: "/courses",
      roles: ["user", "staff", "admin", "tech"],
    },
    {
      label: "Certificates",
      icon: CheckCircle2,
      to: "/certificates",
      activePath: "/certificates",
      roles: ["user", "staff", "admin", "tech"],
    },
    {
      label: "Analytics",
      icon: BarChart,
      to: "/analytics",
      activePath: "/analytics",
      roles: ["user", "staff", "admin", "tech"],
    },
    {
      label: "Support",
      icon: LifeBuoy,
      to: "/support/tickets",
      activePath: "/support",
      roles: ["user", "staff", "admin", "tech"]
    },
  ];
  
  const adminLinks = [
    {
      label: "Admin Courses",
      icon: Book,
      to: "/admin/courses",
      activePath: "/admin/courses",
      roles: ["staff", "admin"],
    },
  ];
  
  const techLinks = [
    {
      label: "Check In",
      icon: BadgeCheck,
      to: "/check-in",
      activePath: "/check-in",
      roles: ["tech"],
    },
    {
      label: "Manage Users",
      icon: Users,
      to: "/admin/users",
      activePath: "/admin/users",
      roles: ["tech"],
    },
    {
      label: "Feature Requests",
      icon: Book,
      to: "/tech/feature-requests",
      activePath: "/tech/feature-requests",
      roles: ["tech", "admin"],
    },
  ];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon">
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="p-0">
        <ScrollArea className="h-screen">
          <SheetHeader className="px-5 pt-5 pb-2.5 flex flex-col items-center">
            <div className="mb-4 mt-2">
              <img 
                src="/lovable-uploads/78ce5b8a-a3f5-4ace-b6b5-c26755a86e21.png"
                alt="NWSISD Logo"
                className="h-10 w-auto mb-2"
                onClick={() => {
                  navigate("/dashboard");
                  setOpen(false);
                }}
                style={{ cursor: "pointer" }}
              />
            </div>
            <SheetTitle>Menu</SheetTitle>
            <SheetDescription>
              Navigate through the application.
            </SheetDescription>
          </SheetHeader>
          <div className="py-4">
            {mainLinks.map(
              (link, index) =>
                link.roles.includes(profile?.role || "user") && (
                  <NavLink
                    key={index}
                    to={link.to}
                    className={({ isActive }) => {
                      const isLinkActive = link.to === "/dashboard" 
                        ? location.pathname === "/dashboard"
                        : isActive || location.pathname.startsWith(link.activePath);
                      
                      return `flex items-center text-sm font-medium py-2.5 px-5 rounded-md transition-colors hover:bg-secondary
                      ${
                        isLinkActive
                          ? "bg-secondary text-foreground"
                          : "text-muted-foreground"
                      }`;
                    }}
                    onClick={() => setOpen(false)}
                  >
                    <link.icon className="w-4 h-4 mr-2" />
                    {link.label}
                  </NavLink>
                )
            )}
            {profile?.role === "admin" || profile?.role === "staff" ? (
              <>
                <div className="my-2 border-b border-b-muted" />
                <div className="px-5 text-xs font-bold uppercase text-muted-foreground">
                  Admin
                </div>
                {adminLinks.map(
                  (link, index) =>
                    link.roles.includes(profile?.role || "user") && (
                      <NavLink
                        key={index}
                        to={link.to}
                        className={({ isActive }) =>
                          `flex items-center text-sm font-medium py-2.5 px-5 rounded-md transition-colors hover:bg-secondary
                          ${
                            isActive
                              ? "bg-secondary text-foreground"
                              : "text-muted-foreground"
                          }`
                        }
                        onClick={() => setOpen(false)}
                      >
                        <link.icon className="w-4 h-4 mr-2" />
                        {link.label}
                      </NavLink>
                    )
                )}
              </>
            ) : null}
            {profile?.role === "admin" || profile?.role === "tech" ? (
              <>
                <div className="my-2 border-b border-b-muted" />
                <div className="px-5 text-xs font-bold uppercase text-muted-foreground">
                  Tech
                </div>
                {techLinks.map(
                  (link, index) =>
                    link.roles.includes(profile?.role || "user") && (
                      <NavLink
                        key={index}
                        to={link.to}
                        className={({ isActive }) =>
                          `flex items-center text-sm font-medium py-2.5 px-5 rounded-md transition-colors hover:bg-secondary
                          ${
                            isActive
                              ? "bg-secondary text-foreground"
                              : "text-muted-foreground"
                          }`
                        }
                        onClick={() => setOpen(false)}
                      >
                        <link.icon className="w-4 h-4 mr-2" />
                        {link.label}
                      </NavLink>
                    )
                )}
              </>
            ) : null}
          </div>
          <SheetHeader className="sticky bottom-0 border-t border-t-muted bg-popover px-5 pt-4 pb-5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex h-8 w-full items-center justify-between rounded-md px-2 text-sm font-medium hover:bg-secondary focus:bg-secondary">
                  {profile?.first_name ? (
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={profile?.avatar_url || profile?.photo_url || ""} />
                        <AvatarFallback>{profile?.first_name?.[0]}{profile?.last_name?.[0]}</AvatarFallback>
                      </Avatar>
                      <span>{profile?.first_name}</span>
                    </div>
                  ) : (
                    "My Account"
                  )}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[180px]">
                <DropdownMenuItem onClick={() => setOpen(false)} asChild>
                  <NavLink to="/profile">
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    signOut();
                    setOpen(false);
                  }}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SheetHeader>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default MobileNav;
